import {
  disciplineMetadata,
  ageCategoryMetadata,
  weightCategoryMetadata,
} from "../custom-disciplines/category-metadata.js";
/**
 * Provides realistic mock data for the layout editor preview.
 *
 * When editing a layout, no live competition data is available, so we inject
 * fake (but realistic) data to let the user see how their layout will look.
 *
 * The shape of the returned object strictly follows `InjectedData`. Every
 * non-context field is populated with at least one item so the editor's mock
 * data panel can expose every binding source without `null` placeholders.
 */

import { v4 as uuidv4 } from "uuid";
import { Prisma as PrismaRemote } from "@prisma-remote/client";
import type {
  CompatibleRemoteAgeCategory as AgeCategory,
  CompatibleRemoteDiscipline as Discipline,
  CompatibleRemoteWeightCategory as WeightCategory,
} from "../types.js";

import type {
  AthleteData,
  GroupData,
  InjectedData,
  LeagueData,
  LevelData,
  MeetData,
  NextLiftData,
  OrganisationData,
  RecordData,
  RefereeLights,
} from "./types.js";

const FIXED_MEET_ID = "mock-meet-1";
const FIXED_ORG_ID = "mock-org-ffforce";

/**
 * Returns a complete `InjectedData` object populated with realistic powerlifting
 * competition data, suitable for the editor preview.
 *
 * @returns Mock injected data covering every non-context source.
 */
export function getMockLayoutData(
  options: {
    athleteCount?: number;
    minAthletesPerGroup?: number;
    maxAthletesPerGroup?: number;
  } = {},
): InjectedData {
  const disciplines = buildMockDisciplines();
  const ageCategories = buildMockAgeCategories();
  const weightCategories = buildMockWeightCategories();
  const leagues = buildMockLeagues();

  const primaryDiscipline = disciplines[0] ?? buildDefaultDiscipline();
  const primaryAgeCategory = ageCategories[2] ?? buildDefaultAgeCategory();
  const primaryLeague = leagues[0] ?? buildDefaultLeague();

  const groupSizes = buildGroupSizes(
    options.athleteCount ?? 40,
    options.minAthletesPerGroup ?? 8,
    options.maxAthletesPerGroup ?? 12,
  );
  const groups = buildMockGroups(groupSizes);
  const athletes = buildMockAthletes({
    groups,
    groupSizes,
    discipline: primaryDiscipline,
    ageCategories,
    weightCategories,
  });

  const organisation = buildMockOrganisation();
  const currentMeet = buildMockMeet({ ligue: primaryLeague });
  const records = buildMockRecords({
    discipline: primaryDiscipline,
    ageCategory: primaryAgeCategory,
    weightCategories,
    organisation,
  });
  const levels = buildMockLevels({
    discipline: primaryDiscipline,
    ageCategory: primaryAgeCategory,
    weightCategories,
  });
  // Next-lift preview athlete (index 2 falls back to first)
  const nextLiftAthlete = athletes[2] ?? athletes[0];
  const nextLiftData: NextLiftData | null = nextLiftAthlete
    ? {
        athlete: nextLiftAthlete,
        movement: "squat",
        attemptNumber: 2,
      }
    : null;
  return {
    currentAthlete: athletes[0] ?? null,
    athletes,
    currentGroup: groups[0] ?? null,
    groups,
    currentMeet,
    displaySettings: {
      lightsEnabled: true,
      showLights: true,
      webClientUrl: "https://example.org",
    },
    timer: "1:00",
    timerSeconds: 60,
    attemptTimers: ["1:00", "0:45", "0:30"],
    attemptTimerSeconds: [60, 45, 30],
    currentMovement: "squat",
    attemptNumber: 2,
    placementIfSuccessful: 1,
    refereeLights: buildMockRefereeLights(),
    nextLiftData,
    records,
    levels,
    ageCategories,
    weightCategories,
    disciplines,
    leagues,
    organisation,
    organisations: [organisation],
    themeColors: {
      primary: "#0056B3",
      primaryText: "#ffffff",
      background: "#ffffff",
      backgroundText: "#000000",
      rankingText: "#000000",
      ranking: "#ffffff",
    },
  };
}

// ---------------------------------------------------------------------------
// Reference data builders
// ---------------------------------------------------------------------------

function buildMockDisciplines(): Discipline[] {
  return [
    {
      ...disciplineMetadata({}),
      id: 1,
      name: "Force Athlétique",
      code: "FA",
      competitionFormat: "FULL_POWER",
    },
    {
      ...disciplineMetadata({}),
      id: 3,
      name: "Powerlifting",
      code: "PL",
      competitionFormat: "FULL_POWER",
    },
    {
      ...disciplineMetadata({}),
      id: 6,
      name: "Développé Couché",
      code: "DC",
      competitionFormat: "SINGLE_LIFT",
    },
    {
      ...disciplineMetadata({}),
      id: 7,
      name: "Bench Press",
      code: "BP",
      competitionFormat: "SINGLE_LIFT",
    },
  ];
}

function buildMockAgeCategories(): AgeCategory[] {
  return [
    {
      ...ageCategoryMetadata({}),
      id: 1,
      name: "Sub-Junior",
      recordName: "Sub-Junior",
      code: "SJR",
      ageMin: 14,
      ageMax: 18,
      disciplineId: 1,
    },
    {
      ...ageCategoryMetadata({}),
      id: 2,
      name: "Junior",
      recordName: "Junior",
      code: "JR",
      ageMin: 19,
      ageMax: 23,
      disciplineId: 1,
    },
    {
      ...ageCategoryMetadata({}),
      id: 3,
      name: "Senior",
      recordName: "Senior",
      code: "SNR",
      ageMin: 24,
      ageMax: 39,
      disciplineId: 1,
    },
    {
      ...ageCategoryMetadata({}),
      id: 4,
      name: "Master 1",
      recordName: "Master 1",
      code: "M1",
      ageMin: 40,
      ageMax: 49,
      disciplineId: 1,
    },
    {
      ...ageCategoryMetadata({}),
      id: 5,
      name: "Master 2",
      recordName: "Master 2",
      code: "M2",
      ageMin: 50,
      ageMax: 59,
      disciplineId: 1,
    },
    {
      ...ageCategoryMetadata({}),
      id: 6,
      name: "Master 3",
      recordName: "Master 3",
      code: "M3",
      ageMin: 60,
      ageMax: 69,
      disciplineId: 1,
    },
    {
      ...ageCategoryMetadata({}),
      id: 7,
      name: "Master 4",
      recordName: "Master 4",
      code: "M4",
      ageMin: 70,
      ageMax: 99,
      disciplineId: 1,
    },
  ];
}

function buildMockWeightCategories(): WeightCategory[] {
  return [
    {
      ...weightCategoryMetadata({}),
      id: 1,
      name: "59 Kg",
      minWeight: 0,
      maxWeight: 59,
      isJunior: false,
      sex: "M",
    },
    {
      ...weightCategoryMetadata({}),
      id: 2,
      name: "66 Kg",
      minWeight: 59,
      maxWeight: 66,
      isJunior: false,
      sex: "M",
    },
    {
      ...weightCategoryMetadata({}),
      id: 3,
      name: "74 Kg",
      minWeight: 66,
      maxWeight: 74,
      isJunior: false,
      sex: "M",
    },
    {
      ...weightCategoryMetadata({}),
      id: 4,
      name: "83 Kg",
      minWeight: 74,
      maxWeight: 83,
      isJunior: false,
      sex: "M",
    },
    {
      ...weightCategoryMetadata({}),
      id: 5,
      name: "93 Kg",
      minWeight: 83,
      maxWeight: 93,
      isJunior: false,
      sex: "M",
    },
    {
      ...weightCategoryMetadata({}),
      id: 6,
      name: "105 Kg",
      minWeight: 93,
      maxWeight: 105,
      isJunior: false,
      sex: "M",
    },
    {
      ...weightCategoryMetadata({}),
      id: 7,
      name: "120 Kg",
      minWeight: 105,
      maxWeight: 120,
      isJunior: false,
      sex: "M",
    },
    {
      ...weightCategoryMetadata({}),
      id: 8,
      name: "+120 Kg",
      minWeight: 120,
      maxWeight: 999,
      isJunior: false,
      sex: "M",
    },
    {
      ...weightCategoryMetadata({}),
      id: 11,
      name: "47 Kg",
      minWeight: 0,
      maxWeight: 47,
      isJunior: false,
      sex: "F",
    },
    {
      ...weightCategoryMetadata({}),
      id: 12,
      name: "52 Kg",
      minWeight: 47,
      maxWeight: 52,
      isJunior: false,
      sex: "F",
    },
    {
      ...weightCategoryMetadata({}),
      id: 13,
      name: "57 Kg",
      minWeight: 52,
      maxWeight: 57,
      isJunior: false,
      sex: "F",
    },
    {
      ...weightCategoryMetadata({}),
      id: 14,
      name: "63 Kg",
      minWeight: 57,
      maxWeight: 63,
      isJunior: false,
      sex: "F",
    },
    {
      ...weightCategoryMetadata({}),
      id: 15,
      name: "69 Kg",
      minWeight: 63,
      maxWeight: 69,
      isJunior: false,
      sex: "F",
    },
    {
      ...weightCategoryMetadata({}),
      id: 16,
      name: "76 Kg",
      minWeight: 69,
      maxWeight: 76,
      isJunior: false,
      sex: "F",
    },
    {
      ...weightCategoryMetadata({}),
      id: 17,
      name: "84 Kg",
      minWeight: 76,
      maxWeight: 84,
      isJunior: false,
      sex: "F",
    },
    {
      ...weightCategoryMetadata({}),
      id: 18,
      name: "+84 Kg",
      minWeight: 84,
      maxWeight: 999,
      isJunior: false,
      sex: "F",
    },
  ];
}

function buildMockLeagues(): LeagueData[] {
  return [
    { id: 1, name: "Île-de-France" },
    { id: 2, name: "Auvergne-Rhône-Alpes" },
    { id: 3, name: "Provence-Alpes-Côte d'Azur" },
    { id: 4, name: "Nouvelle-Aquitaine" },
  ];
}

function buildMockMeet({ ligue }: { ligue: LeagueData }): MeetData {
  return {
    id: FIXED_MEET_ID,
    name: "Championnat Régional Île-de-France 2025",
    city: "Paris",
    startdate: new Date("2025-03-15"),
    enddate: new Date("2025-03-16"),
    created_at: new Date("2025-01-10"),
    ligue_id: ligue.id,
    organisationId: FIXED_ORG_ID,
    licenseKey: "LEGACY",
    streamUrl: "https://example.com/stream",
    themeName: null,
    isPublic: true,
    isOver: false,
    ligue,
  };
}

// ---------------------------------------------------------------------------
// Groups & athletes
// ---------------------------------------------------------------------------

function buildMockGroups(groupSizes: number[]): GroupData[] {
  return groupSizes.map<GroupData>((_, groupIndex) => ({
    id: uuidv4(),
    name: `Groupe ${groupIndex + 1}`,
    meet_id: FIXED_MEET_ID,
    plateauNumber: 1,
    groupNumber: groupIndex + 1,
    observations: groupIndex === 0 ? "Groupe d'ouverture" : null,
    secretary: "Jean DUVAL",
    referee1: "Pierre MOREL",
    referee2: "Claire ROUX",
    centralReferee: "Marc DUBOIS",
    jury: "A. BLANC, B. NOIR, C. VERT",
    share: true,
    competitionFormat: "FULL_POWER",
    customDisciplineGroupId: null,
    selected: groupIndex === 0,
  }));
}

interface MockAthletesContext {
  groups: GroupData[];
  groupSizes: number[];
  discipline: Discipline;
  ageCategories: AgeCategory[];
  weightCategories: WeightCategory[];
}

function buildMockAthletes(ctx: MockAthletesContext): AthleteData[] {
  const athletes: AthleteData[] = [];
  let athleteCounter = 1;

  for (let groupIndex = 0; groupIndex < ctx.groups.length; groupIndex += 1) {
    const group = ctx.groups[groupIndex]!;
    const size = ctx.groupSizes[groupIndex] ?? 0;

    for (let i = 0; i < size; i += 1) {
      const sex = Math.random() < 0.5 ? "M" : "F";
      const bodyweight = getRandomStepValue(52.5, 125, 2.5);
      const squats = buildLiftAttempts(100, 250);
      const benchs = buildLiftAttempts(80, 180);
      const deadlifts = buildLiftAttempts(150, 300);

      const ageCategory = ctx.ageCategories[2] ?? buildDefaultAgeCategory();
      const weightCategory =
        ctx.weightCategories.find(
          (w) =>
            w.sex === sex &&
            bodyweight > w.minWeight &&
            bodyweight <= w.maxWeight,
        ) ?? buildDefaultWeightCategory(sex);

      athletes.push(
        buildMockAthlete({
          id: uuidv4(),
          lastname: `Nom ${athleteCounter}`,
          firstname: `Prenom ${athleteCounter}`,
          sex,
          bodyweight,
          lot: athleteCounter,
          club: `Club ${groupIndex + 1}`,
          squats,
          benchs,
          deadlifts,
          group_id: group.id,
          discipline: ctx.discipline,
          ageCategory,
          weightCategory,
        }),
      );
      athleteCounter += 1;
    }
  }

  return athletes;
}

interface BuildAthleteParams {
  id: string;
  lastname: string;
  firstname: string;
  sex: string;
  bodyweight: number;
  lot: number;
  club: string;
  squats: [number, number, number];
  benchs: [number, number, number];
  deadlifts: [number, number, number];
  group_id: string;
  discipline: Discipline;
  ageCategory: AgeCategory;
  weightCategory: WeightCategory;
}

function buildMockAthlete(params: BuildAthleteParams): AthleteData {
  const totalkg = params.squats[2] + params.benchs[2] + params.deadlifts[2];

  return {
    id: params.id,
    lastname: params.lastname,
    firstname: params.firstname,
    club: params.club,
    licence: `FR-${params.id.slice(0, 8).toUpperCase()}`,
    sex: params.sex,
    birthDate: new Date("1995-06-15"),
    bodyweight: params.bodyweight,
    lot: params.lot,
    group_id: params.group_id,
    isDisqualified: false,

    squat1: new PrismaRemote.Decimal(params.squats[0]),
    squat1Status: "LIFT",
    squat1Auto: false,
    squat1Overturn: false,
    squat1LeftColor: "white",
    squat1ChiefColor: "white",
    squat1RightColor: "white",
    squat1OverturnColor: "",
    squat2: new PrismaRemote.Decimal(params.squats[1]),
    squat2Status: params.lot === 1 ? "ANNONCED" : "LIFT",
    squat2Auto: false,
    squat2Overturn: false,
    squat2LeftColor: "white",
    squat2ChiefColor: "white",
    squat2RightColor: "white",
    squat2OverturnColor: "",
    squat3: new PrismaRemote.Decimal(params.squats[2]),
    squat3Status: "ANNONCED",
    squat3Auto: false,
    squat3Overturn: false,
    squat3LeftColor: "",
    squat3ChiefColor: "",
    squat3RightColor: "",
    squat3OverturnColor: "",

    bench1: new PrismaRemote.Decimal(params.benchs[0]),
    bench1Status: "ANNONCED",
    bench1Auto: false,
    bench1Overturn: false,
    bench1LeftColor: "",
    bench1ChiefColor: "",
    bench1RightColor: "",
    bench1OverturnColor: "",
    bench2: new PrismaRemote.Decimal(params.benchs[1]),
    bench2Status: "ANNONCED",
    bench2Auto: false,
    bench2Overturn: false,
    bench2LeftColor: "",
    bench2ChiefColor: "",
    bench2RightColor: "",
    bench2OverturnColor: "",
    bench3: new PrismaRemote.Decimal(params.benchs[2]),
    bench3Status: "ANNONCED",
    bench3Auto: false,
    bench3Overturn: false,
    bench3LeftColor: "",
    bench3ChiefColor: "",
    bench3RightColor: "",
    bench3OverturnColor: "",

    deadlift1: new PrismaRemote.Decimal(params.deadlifts[0]),
    deadlift1Status: "ANNONCED",
    deadlift1Auto: false,
    deadlift1Overturn: false,
    deadlift1LeftColor: "",
    deadlift1ChiefColor: "",
    deadlift1RightColor: "",
    deadlift1OverturnColor: "",
    deadlift2: new PrismaRemote.Decimal(params.deadlifts[1]),
    deadlift2Status: "ANNONCED",
    deadlift2Auto: false,
    deadlift2Overturn: false,
    deadlift2LeftColor: "",
    deadlift2ChiefColor: "",
    deadlift2RightColor: "",
    deadlift2OverturnColor: "",
    deadlift3: new PrismaRemote.Decimal(params.deadlifts[2]),
    deadlift3Status: "ANNONCED",
    deadlift3Auto: false,
    deadlift3Overturn: false,
    deadlift3LeftColor: "",
    deadlift3ChiefColor: "",
    deadlift3RightColor: "",
    deadlift3OverturnColor: "",

    totalkg: new PrismaRemote.Decimal(totalkg),
    totalAnnonced: new PrismaRemote.Decimal(totalkg + 5),
    rank: params.lot,
    squatHeight: 15,
    benchHeight: 12,
    secuHeight: 10,
    cales: params.lot % 2 === 0 ? 2 : 0,
    rackPosition: "EXT",

    ageCategoryId: params.ageCategory.id,
    weightCategoryId: params.weightCategory.id,
    points: new PrismaRemote.Decimal(0),
    disciplineId: params.discipline.id,

    discipline: params.discipline,
    ageCategory: params.ageCategory,
    weightCategory: params.weightCategory,
    powerscoutLifterId: null,
    powerscoutBests: {
      best_squat_kg: new PrismaRemote.Decimal(200),
      best_bench_kg: new PrismaRemote.Decimal(140),
      best_deadlift_kg: new PrismaRemote.Decimal(220),
      best_total_kg: new PrismaRemote.Decimal(560),
    } as NonNullable<AthleteData["powerscoutBests"]>,
  };
}

// ---------------------------------------------------------------------------
// Referees, records, levels
// ---------------------------------------------------------------------------

function buildMockRefereeLights(): RefereeLights[] {
  return [
    { type: "left", passed: true, color: "white", juryEnabled: false },
    { type: "chief", passed: true, color: "white", juryEnabled: false },
    { type: "right", passed: false, color: "red", juryEnabled: false },
    { type: "jury_1", passed: false, color: "", juryEnabled: true },
    { type: "jury_2", passed: false, color: "", juryEnabled: true },
    { type: "jury_3", passed: false, color: "", juryEnabled: false },
  ];
}

/**
 * Builds a mock competition organisation (FFForce-like identity).
 *
 * @returns OrganisationData for InjectedData.organisation and nested records.
 */
function buildMockOrganisation(): OrganisationData {
  return {
    id: FIXED_ORG_ID,
    name: "FFForce",
    code: "FFFORCE",
    countryCode: "FR",
    recordLabel: "Record de France",
    recordColor: "#002395",
    recordBadgeUrl: null,
    parentId: null,
  };
}

function buildMockRecords({
  discipline,
  ageCategory,
  weightCategories,
  organisation,
}: {
  discipline: Discipline;
  ageCategory: AgeCategory;
  weightCategories: WeightCategory[];
  organisation: OrganisationData;
}): RecordData[] {
  const movements = ["squat", "bench", "deadlift"] as const;
  const sample = weightCategories.slice(0, 3);
  return sample.map((weightCategory, index) => ({
    id: index + 1,
    movement: movements[index] ?? "squat",
    weight: 200 + index * 25,
    ageCategoryId: ageCategory.id,
    weightCategoryId: weightCategory.id,
    dateLocation: "Paris - 2024",
    athleteName: `Champion ${index + 1}`,
    disciplineId: discipline.id,
    sex: weightCategory.sex,
    sheetDate: "2024-06-01",
    organisationId: organisation.id,
    ageCategory,
    weightCategory,
    discipline,
    organisation,
  }));
}

function buildMockLevels({
  discipline,
  ageCategory,
  weightCategories,
}: {
  discipline: Discipline;
  ageCategory: AgeCategory;
  weightCategories: WeightCategory[];
}): LevelData[] {
  const sample = weightCategories.slice(0, 3);
  return sample.flatMap((weightCategory, index) => [
    {
      id: index * 2 + 1,
      minTotal: 400,
      maxTotal: 499.99,
      name: "R3",
      ageCategoryId: ageCategory.id,
      weightCategoryId: weightCategory.id,
      disciplineId: discipline.id,
      sheetDate: "2024-09-01",
      ageCategory,
      weightCategory,
      discipline,
    },
    {
      id: index * 2 + 2,
      minTotal: 500,
      maxTotal: 599.99,
      name: "R2",
      ageCategoryId: ageCategory.id,
      weightCategoryId: weightCategory.id,
      disciplineId: discipline.id,
      sheetDate: "2024-09-01",
      ageCategory,
      weightCategory,
      discipline,
    },
  ]);
}

// ---------------------------------------------------------------------------
// Defaults & helpers
// ---------------------------------------------------------------------------

function buildDefaultAgeCategory(): AgeCategory {
  return {
    ...ageCategoryMetadata({}),
    id: 1,
    name: "Senior",
    recordName: "Senior",
    code: "SNR",
    ageMin: 24,
    ageMax: 39,
    disciplineId: 1,
  };
}

function buildDefaultDiscipline(): Discipline {
  return {
    ...disciplineMetadata({}),
    id: 1,
    name: "Force Athlétique",
    code: "FA",
    competitionFormat: "FULL_POWER",
  };
}

function buildDefaultLeague(): LeagueData {
  return { id: 1, name: "Île-de-France" };
}

function buildDefaultWeightCategory(sex: string): WeightCategory {
  return sex === "F"
    ? {
        ...weightCategoryMetadata({}),
        id: 15,
        name: "69 Kg",
        minWeight: 63,
        maxWeight: 69,
        isJunior: false,
        sex: "F",
      }
    : {
        ...weightCategoryMetadata({}),
        id: 4,
        name: "83 Kg",
        minWeight: 74,
        maxWeight: 83,
        isJunior: false,
        sex: "M",
      };
}

function buildGroupSizes(
  total: number,
  minSize: number,
  maxSize: number,
): number[] {
  const sizes: number[] = [];
  let remaining = total;

  while (remaining > 0) {
    if (remaining <= maxSize) {
      sizes.push(remaining);
      break;
    }

    const possibleSizes: number[] = [];
    for (let size = minSize; size <= maxSize; size += 1) {
      const nextRemaining = remaining - size;
      if (nextRemaining === 0 || nextRemaining >= minSize) {
        possibleSizes.push(size);
      }
    }

    if (possibleSizes.length === 0) {
      sizes.push(remaining);
      break;
    }

    const selectedSize =
      possibleSizes[randomInt(0, possibleSizes.length - 1)] ?? minSize;
    sizes.push(selectedSize);
    remaining -= selectedSize;
  }

  return sizes;
}

function buildLiftAttempts(
  minStart: number,
  maxStart: number,
): [number, number, number] {
  const first = getRandomLiftOpening(minStart, maxStart);
  const second = first + getRandomStepValue(2.5, 30, 2.5);
  const third = second + getRandomStepValue(2.5, 30, 2.5);

  return [first, second, third];
}

function getRandomLiftOpening(min: number, max: number): number {
  const candidates: number[] = [];

  for (let value = min; value <= max; value += 2.5) {
    const normalized = Math.round(value * 10) / 10;
    const unit = normalized % 10;
    if (unit === 0 || unit === 2.5 || unit === 5) {
      candidates.push(normalized);
    }
  }

  if (candidates.length === 0) {
    return min;
  }

  return candidates[randomInt(0, candidates.length - 1)] ?? min;
}

function getRandomStepValue(min: number, max: number, step: number): number {
  const stepCount = Math.floor((max - min) / step);
  const randomStep = randomInt(0, stepCount);
  return Math.round((min + randomStep * step) * 10) / 10;
}

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
